<script lang="ts" setup>
import { AisRefinementList } from "vue-instantsearch/vue3/es";
import type { Category, CategoryLevel } from "~/types/common";

// -----------------------
// props & emits
// -----------------------
const props = defineProps<{
  attribute: string;
  attributeName: string;
  isOpen?: boolean;
  singleValue?: boolean;
}>();

// -----------------------
// composables
// -----------------------
const { getUrlHash } = useHelpers();
const { cmsPagePath } = useMainStore();

// -----------------------
// refs
// -----------------------
let refine: Function;
const isOpen = props.isOpen ? ref(true) : ref(false);
const initialized = ref<boolean>(false);
const expandedCategories = ref<string[]>([]);
const tree = ref<CategoryLevel>({
  path: "",
  label: "",
  count: 0,
  children: {},
  isRefined: false,
  order: "00000",
});

const root = computed(() => {
  const mainCategories = Object.keys(tree.value.children);

  // Render the subcategories if there is only one main category
  if (mainCategories.length === 1) {
    return tree.value.children[mainCategories[0]];
  }

  return tree.value;
});

// -----------------------
// helper functions
// -----------------------
const addCategory = (level: CategoryLevel, category: Category) => {
  const order = category.label.substring(0, category.label.indexOf("|"));
  const label = category.label.substring(category.label.indexOf("|") + 1);
  const levels = label.split(" > ");
  const topLevel = levels.shift() ?? "";

  if (!(topLevel in level.children)) {
    level.children[topLevel] = {
      path: level.path === "" ? topLevel : level.path + " > " + topLevel,
      label: topLevel,
      children: {},
      order,
    };
  }

  if (levels.length === 0) {
    level.children[topLevel].count = category.count;
    level.children[topLevel].isRefined = category.isRefined;
    level.children[topLevel].order = order;
  }

  if (levels.length > 0) {
    const subcategory = { ...category };
    subcategory.label = order + "|" + levels.join(" > ");
    addCategory(level.children[topLevel], subcategory);
  }
};

const clearDescendantRefinements = (node: CategoryLevel) => {
  for (const label in node.children) {
    const category = node.children[label];

    if (category.isRefined) {
      category.isRefined = false;
      refine(`${category.order}|${category.path}`);
    }

    if (Object.keys(category.children).length > 0) {
      clearDescendantRefinements(category);
    }
  }
};

const walkTree = (
  root: CategoryLevel,
  callback: Function,
): CategoryLevel | null => {
  for (const label in root.children) {
    const category = root.children[label];
    const result = callback(category);

    if (result === false) {
      return category;
    }

    if (Object.keys(category.children).length > 0) {
      const childrenResult = walkTree(category, callback);

      if (childrenResult !== null) {
        return childrenResult;
      }
    }
  }

  return null;
};

const findCategoryByPath = (path: string) => {
  return walkTree(tree.value, (category: CategoryLevel) => {
    if (category.path === path) {
      return false;
    }
  });
};

const clearAncestorRefinements = (node: CategoryLevel) => {
  const pathParts = node.path.split(" > ");

  if (pathParts.length === 1) {
    return;
  }

  pathParts.pop();
  const parentPath = pathParts.join(" > ");
  const parentCategory = findCategoryByPath(parentPath);

  if (parentCategory) {
    if (parentCategory.isRefined) {
      parentCategory.isRefined = false;
      refine(`${parentCategory.order}|${parentCategory.path}`);
    }

    clearAncestorRefinements(parentCategory);
  }
};

const clearRefinements = (root?: CategoryLevel) => {
  if (!root) {
    root = tree.value;
  }

  for (const label in root.children) {
    const category = root.children[label];
    if (category.isRefined) {
      category.isRefined = false;
      refine(`${category.order}|${category.path}`);
    } else if (Object.keys(category.children).length > 0) {
      clearRefinements(category);
    }
  }
};

const initRefinement = (categories: Category[], refineFn: Function) => {
  const urlHash = getUrlHash([]);
  refine = refineFn;

  for (const category of categories) {
    addCategory(tree.value, category);
  }

  expandedCategories.value = getExpandedCategories(urlHash);

  if (cmsPagePath) {
    expandedCategories.value.push(cmsPagePath);
  }

  initialized.value = true;
};

const toggleRefine = (category: CategoryLevel) => {
  if (props.singleValue && !category.isRefined) {
    // Clear previous refinements to ensure only one filter is selected
    clearRefinements();
  }

  category.isRefined = !category.isRefined;
  clearAncestorRefinements(category);
  clearDescendantRefinements(category);
  refine(`${category.order}|${category.path}`);
};

const getExpandedCategories = (urlHash?: string) => {
  const raw = localStorage.getItem("expandedCategories");
  const parsed = raw ? JSON.parse(raw) : {};

  if (!urlHash) {
    return parsed;
  }

  return parsed[urlHash] ?? [];
};

const toggleCategory = (path: string) => {
  if (expandedCategories.value.includes(path)) {
    expandedCategories.value.splice(expandedCategories.value.indexOf(path), 1);
  } else {
    expandedCategories.value.push(path);
  }

  const urlHash = getUrlHash([]);
  const updatedExpandedCategories = getExpandedCategories();

  updatedExpandedCategories[urlHash] = expandedCategories.value;
  localStorage.setItem(
    "expandedCategories",
    JSON.stringify(updatedExpandedCategories),
  );
};
</script>

<template>
  <div class="bg-white rounded-16 p-4">
    <div
      class="flex flex-row cursor-pointer"
      @mousedown.prevent="isOpen = !isOpen"
    >
      <div class="basis-5/6 font-bold text-xl flex flex-row font-lora">
        <span>{{ attributeName }}</span>
      </div>
      <div class="basis-1/6 text-right">
        <ProductListFilterArrow :inverted="isOpen" />
      </div>
    </div>

    <div class="pt-3" :class="{ hidden: !isOpen }">
      <AisRefinementList
        v-slot="{ items, refine: refineFn }"
        :attribute="attribute"
        :sort-by="['name:asc']"
        operator="or"
        :limit="1000"
      >
        {{ initRefinement(items, refineFn) }}
      </AisRefinementList>
      <ProductListHierarchicalFilterLevel
        v-if="initialized"
        :level="1"
        :initialized="initialized"
        :root="root"
        :expanded-categories="expandedCategories"
        @refine="toggleRefine"
        @toggle="toggleCategory"
      ></ProductListHierarchicalFilterLevel>
    </div>
  </div>
</template>
